import {
  confirmDialog,
  CustomButton,
  CustomCollapseCard,
  CustomTable,
  successDialog,
  Text,
} from '@src/components';
import React, { useCallback, useEffect, useState } from 'react';
import '@src/styles/pendingApproval/balanceOrder.scss';
import { DownCircleOutlined, UpCircleOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import {
  getLoadBalancesForStatusWithUpdaterUserName,
  loadBalancesDelete,
  loadBalancesUpdate,
} from '@src/store/slice/balanceUploadSlice';
import dayjs from '@src/utils/day';
import OrderSummaryBalanceModal from '../ticketRestaurant/myOrders/OrderSummaryBalanceModal';
import { setSelectedOrder } from '@src/store/slice/orderSlice';
import { useHistory } from 'react-router-dom';
import { moneyFormat } from '@src/utils/utils';
import { useMediaQuery } from '@src/hooks/useMediaQuery';
import { getPendingNotifications } from '@src/store/slice/notificationSlice';
import PropTypes from 'prop-types';

function getExpandIcon() {
  return ({ expanded, onExpand, record }) =>
    expanded ? (
      <UpCircleOutlined style={{ fontSize: '24px' }} onClick={(e) => onExpand(record, e)} />
    ) : (
      <DownCircleOutlined style={{ fontSize: '24px' }} onClick={(e) => onExpand(record, e)} />
    );
}

function getExpandedRowRender(handleBalanceCancel, handleApproved) {
  return (record) => {
    return (
      <div className="custom-expanded-row" key={record.id}>
        <div className="colum-title">
          <span>İŞLEM TARİHİ:</span>{' '}
          <span>{dayjs(record.updateTime).format('DD/MM/YYYY HH:mm')}</span>
        </div>
        <div className="colum-title">
          <span>KART ADEDİ:</span> <span>{record.totalCount}</span>
        </div>
        <div className="colum-title">
          <span>SİPARİŞ TUTARI:</span>{' '}
          <span>{record.totalPrice ? `${moneyFormat(record.totalPrice, false)} TL` : '0'}</span>
        </div>

        <div className="mobile-action-btn">
          <CustomButton
            id="pendingApproval-balanceOrderList-custom-btn-cancel"
            onClick={() => handleBalanceCancel(record)}
            className="custom-btn-cancel"
            buttonAuthName="AwaitingApprovalCancel"
          >
            <Text t="İPTAL" />
          </CustomButton>
          <CustomButton
            id="pendingApproval-balanceOrderList-custom-btn-approved"
            buttonAuthName="ApprovalAuthorizationGroup"
            onClick={() => handleApproved(record)}
            className="custom-btn-approved"
          >
            <Text t="ONAY" />
          </CustomButton>
        </div>
      </div>
    );
  };
}

const DEFAULT_TABLE_PROPERTY = {
  currentPage: 1,
  page: 1,
  pageSize: 10,
  totalCount: 0,
};

const BalanceOrderList = ({ isMarket = false }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const isMobil = useMediaQuery({ query: '(max-width: 1179.98px)' });

  const [loadBalanceList, setLoadBalanceList] = useState([]);
  const [draftedTableProperty, setDraftedTableProperty] = useState(DEFAULT_TABLE_PROPERTY);
  const { processType } = useSelector((state) => state?.packages);
  const { selectedCustomer } = useSelector((state) => state?.customer);

  const [expandedRows, setExpandedRows] = useState([]);
  const [orderSummaryBalanceModalVisible, setOrderSummaryBalanceModalVisible] = useState(false);

  useEffect(() => {
    if (processType === 1 || processType === 0) loadBalances();
  }, [processType]);

  useEffect(() => {
    getList();
  }, []);

  const loadBalances = useCallback(() => {
    getList();
  }, [dispatch, selectedCustomer]);

  const getList = async (pageNumber = 1, pageSize = 10) => {
    const body = {
      customerId: selectedCustomer?.customerId,
      orderStatus: 15,
      pageNumber,
      pageSize,
      vouId: isMarket ? 11 : 1,
    };
    const action = await dispatch(getLoadBalancesForStatusWithUpdaterUserName(body));
    if (getLoadBalancesForStatusWithUpdaterUserName.fulfilled.match(action)) {
      setLoadBalanceList(action?.payload?.data?.items || []);
      setDraftedTableProperty(action?.payload?.data?.pagedProperty || DEFAULT_TABLE_PROPERTY);
    } else {
      setLoadBalanceList([]);
      setDraftedTableProperty(DEFAULT_TABLE_PROPERTY);
    }
  };

  const handleBalanceTableChange = useCallback(
    (pageNumber, pageSize) => {
      getList(pageNumber, pageSize);
    },
    [dispatch, selectedCustomer?.customerId, isMarket],
  );

  const handleBalanceDetail = (record) => {
    dispatch(setSelectedOrder(record));
    setOrderSummaryBalanceModalVisible(true);
  };

  const handleApproved = async (item) => {
    const isBefore = item?.activationDate < dayjs().format('YYYY-MM-DDTHH:mm:ss')
    const currentMonth = dayjs().format('MM');
    const body = {
      invoiceMonth: isBefore ? currentMonth : item?.invoiceMonth,
      PendingApproval: true,
      orderStatus: 10,
      id: item?.id,
    };
    const action = await dispatch(loadBalancesUpdate(body));
    if (loadBalancesUpdate.fulfilled.match(action)) {
      dispatch(getPendingNotifications({}));
      history.push({
        pathname: '/ticket-restaurant/balance-upload',
        state: { id: item?.id },
      });
    }
  };

  const handleBalanceCancel = async (id) => {
    const body = { id, CancelledByUser: true };

    confirmDialog({
      title: <Text t="attention" />,
      message: 'İptal etmek istediğinize emin misiniz?',
      onOk: async () => {
        const action = await dispatch(loadBalancesDelete(body));
        if (loadBalancesDelete.fulfilled.match(action)) {
          await getList();
          successDialog({
            title: <Text t="successfullySent" />,
            message: action?.payload?.message || 'İşleminiz Başarıyla Gerçekleşmiştir',
            onOk: async () => {
              await loadBalances();
            },
            onCancel: () => {},
          });
        }
      },
    });
  };

  const columnsBalance = [
    {
      title: 'TASLAK NO',
      dataIndex: 'id',
      key: 'id',
      width: '20%',
      render: (text, record) => {
        return (
          <div
            className="balance-order-list-order-number"
            onClick={() => handleBalanceDetail(record)}
          >
            {text}
          </div>
        );
      },
    },
    {
      title: 'İŞLEMİ YAPAN',
      dataIndex: 'userFullName',
      key: 'userFullName',
    },
    {
      title: 'İŞLEM TARİHİ',
      dataIndex: 'updateTime',
      key: 'updateTime',
      width: '20%',
      responsive: ['md'],
      render: (text) => {
        return <div>{dayjs(text).format('DD/MM/YYYY HH:mm')}</div>;
      },
    },
    {
      title: 'KART ADEDİ',
      dataIndex: 'totalCount',
      key: 'totalCount',
      width: '20%',
      responsive: ['md'],
    },
    {
      title: 'SİPARİŞ TUTARI',
      dataIndex: 'totalPrice',
      key: 'totalPrice',
      width: '20%',
      responsive: ['md'],
      render: (text) => <span>{text ? `${moneyFormat(text, false)} TL` : '0'}</span>,
    },
    {
      title: '',
      dataIndex: 'action',
      key: 'action',
      align: 'center',
      responsive: ['md'],
      render: (_, record) => {
        return (
          <div className="row-btn-actions">
            <CustomButton
              id="pendingApproval-balanceOrderList-custom-second-btn-cancel"
              onClick={() => handleBalanceCancel(record?.id)}
              className="custom-btn-cancel"
              buttonAuthName="AwaitingApprovalCancel"
            >
              <Text t="İPTAL" />
            </CustomButton>
            <CustomButton
              id="pendingApproval-balanceOrderList-custom-second-btn-approved"
              onClick={() => handleApproved(record)}
              className="custom-btn-approved"
              buttonAuthName="ApprovalAuthorizationGroup"
            >
              <Text t="ONAY" />
            </CustomButton>
          </div>
        );
      },
    },
  ];

  return (
    <CustomCollapseCard
      className="balance-order-card"
      cardTitle={<Text t="Bakiye Siparişi Onayı" />}
    >
      <CustomTable
        pagination={
          draftedTableProperty?.totalCount > 10
            ? {
                total: draftedTableProperty?.totalCount,
                pageSize: draftedTableProperty?.pageSize,
                current: draftedTableProperty?.currentPage,
                onChange: (pageNumber, pageSize) => handleBalanceTableChange(pageNumber, pageSize),
                defaultCurrent: 1,
              }
            : false
        }
        isScroll={false}
        dataSource={loadBalanceList}
        columns={columnsBalance}
        rowKey={(record) => `list-${record?.id}`}
        expandedRowKeys={expandedRows}
        expandable={{
          showExpandColumn: false,
          expandIconColumnIndex: 7,
          onExpand: (expanded, record, key) =>
            expandedRows === key ? setExpandedRows(null) : setExpandedRows(key),
          expandIcon: isMobil ? getExpandIcon() : null,
          expandedRowRender: isMobil
            ? getExpandedRowRender(handleBalanceCancel, handleApproved)
            : null,
        }}
      />
      <OrderSummaryBalanceModal
        modalVisible={orderSummaryBalanceModalVisible}
        handleModalVisible={setOrderSummaryBalanceModalVisible}
        isPending={true}
      />
    </CustomCollapseCard>
  );
};

BalanceOrderList.propTypes = {
  isMarket: PropTypes.bool,
};

export default BalanceOrderList;
